import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react'
import { IUserWithModules, ModuleType, IModuleConfig } from '../types/modules'
import { getAvailableModules } from '../config/moduleConfig'

interface UserModuleContextType {
  user: IUserWithModules | null
  availableModules: IModuleConfig[]
  hasModule: (moduleType: ModuleType) => boolean
  hasPermission: (moduleType: ModuleType, permission: string) => boolean
  isLoading: boolean
}

const UserModuleContext = createContext<UserModuleContextType | undefined>(undefined)

interface UserModuleProviderProps {
  children: ReactNode
  user: IUserWithModules | null
  isLoading: boolean
}

export const UserModuleProvider: React.FC<UserModuleProviderProps> = ({
  children,
  user,
  isLoading
}) => {
  const [enabledModuleTypes, setEnabledModuleTypes] = useState<ModuleType[]>([])

  useEffect(() => {
    // Map frontend module names to backend module names
    const moduleMapping: Record<string, ModuleType> = {
      medinsight: ModuleType.LITERATURE_WATCH,
      literature_watch: ModuleType.LITERATURE_WATCH,
      teleexpertise: ModuleType.TELEEXPERTISE,
      lab: ModuleType.LAB,
      patients: ModuleType.PATIENTS,
      home: ModuleType.HOME,
      training: ModuleType.TRAINING,
      blot_analysis: ModuleType.BLOT_ANALYSIS,
      admin: ModuleType.ADMIN,
    }

    // Get modules from API or localStorage (for mock mode)
    let modules = user?.modules
      ?.filter(module => module.isEnabled)
      ?.map(module => module.moduleType) || []

    // If no modules from API, check localStorage (fallback)
    if (modules.length === 0) {
      const localModules = localStorage.getItem('userModules')
      if (localModules) {
        try {
          const parsed = JSON.parse(localModules)
          modules = parsed.map((m: string) => moduleMapping[m] || m)
        } catch (_e) {
          // ignore parse errors
        }
      }
    }
    // Hidden app-wide for now, regardless of each user's own DB-enabled
    // modules — to bring it back later, just drop this filter.
    setEnabledModuleTypes(modules.filter((m) => m !== ModuleType.PATIENTS))
  }, [user])

  const availableModules = getAvailableModules(enabledModuleTypes, user?.role)

  const hasModule = (moduleType: ModuleType): boolean => {
    return enabledModuleTypes.includes(moduleType)
  }

  const hasPermission = (moduleType: ModuleType, permission: string): boolean => {
    const userModule = user?.modules?.find(
      module => module.moduleType === moduleType && module.isEnabled
    )
    return userModule?.permissions?.includes(permission) || false
  }

  return (
    <UserModuleContext.Provider
      value={{
        user,
        availableModules,
        hasModule,
        hasPermission,
        isLoading
      }}
    >
      {children}
    </UserModuleContext.Provider>
  )
}

export const useUserModules = () => {
  const context = useContext(UserModuleContext)
  if (context === undefined) {
    throw new Error('useUserModules must be used within a UserModuleProvider')
  }
  return context
}